/**
 * Engagement wizard — onboard a whole client in one form.
 *
 * Client name + URLs + cadence → creates the product, queues an immediate
 * hosted baseline scan, and (optionally) arms a recurring monitoring
 * schedule. One click stands up everything a new engagement needs.
 */

import { useState } from 'preact/hooks';
import { DashboardApi } from '../api.js';
import type { AuthOrg } from '../auth.js';
import { Banner } from '../components/Banner.js';

const api = new DashboardApi();

type Cadence = 'none' | 'daily' | 'weekly' | 'monthly';

interface Result {
  product: { id: string; slug: string };
  baselineScan?: { scanId: string; jobId: string; status: string } | { error: string };
  schedule?: { id: string; cadence: string; nextRun: string | null } | { error: string };
}

const hasError = (v: unknown): v is { error: string } =>
  typeof v === 'object' && v !== null && 'error' in v;

export function Engage({ org }: { org: AuthOrg }): preact.ComponentChildren {
  const [clientName, setClientName] = useState('');
  const [urlsText, setUrlsText] = useState('');
  const [cadence, setCadence] = useState<Cadence>('weekly');
  const [failOnRegression, setFailOnRegression] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<Result | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: Event) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const urls = urlsText
        .split(/[\s,]+/)
        .map((u) => u.trim())
        .filter(Boolean);
      if (!clientName.trim()) {
        setError('Enter a client name.');
        return;
      }
      if (urls.length === 0) {
        setError('Enter at least one URL.');
        return;
      }
      const r = await api.createEngagement(org.id, {
        clientName: clientName.trim(),
        urls,
        cadence,
        ...(cadence !== 'none' ? { failOnRegression } : {}),
      });
      setResult(r);
      setClientName('');
      setUrlsText('');
    } catch (e) {
      const detail = e instanceof Error ? e.message : String(e);
      setError(
        `Engagement couldn't be created — try again, and if this keeps happening contact support@wholisphere.ai. (${detail})`,
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div class="page-header">
        <h1>New engagement</h1>
        <p class="muted">
          Onboard a client in one step: this creates the product, runs an immediate baseline scan on
          our hosted fleet, and sets up recurring monitoring. No CLI, no setup.
        </p>
      </div>

      {error && <Banner variant="warn">{error}</Banner>}

      {result ? (
        <div class="card">
          {/* Concise announcement for screen readers; the card below carries the detail. */}
          <output class="sr-only">Engagement created. Baseline scan queued.</output>
          <h2>Engagement created ✓</h2>
          <ul style={{ lineHeight: 1.9 }}>
            <li>
              <strong>Product:</strong> <code>{result.product.slug}</code>
            </li>
            <li>
              <strong>Baseline scan:</strong>{' '}
              {hasError(result.baselineScan) ? (
                <span style={{ color: 'var(--danger, #f87171)' }}>
                  failed — {result.baselineScan.error}
                </span>
              ) : result.baselineScan ? (
                <>
                  queued (<code>{result.baselineScan.scanId.slice(0, 8)}</code>) — watch it on the{' '}
                  Hosted scans page
                </>
              ) : (
                '—'
              )}
            </li>
            <li>
              <strong>Monitoring:</strong>{' '}
              {!result.schedule ? (
                'none'
              ) : hasError(result.schedule) ? (
                <span style={{ color: 'var(--danger, #f87171)' }}>
                  failed — {result.schedule.error}
                </span>
              ) : (
                <>
                  {result.schedule.cadence}, next run{' '}
                  {result.schedule.nextRun
                    ? new Date(result.schedule.nextRun).toLocaleString()
                    : '—'}
                </>
              )}
            </li>
          </ul>
          <p class="muted small" style={{ marginTop: 8 }}>
            When the baseline finishes, review + publish its VPAT from the Review &amp; publish
            page.
          </p>
          <button type="button" onClick={() => setResult(null)} style={{ marginTop: 8 }}>
            Onboard another
          </button>
        </div>
      ) : (
        <div class="card">
          <form onSubmit={submit}>
            <div class="field">
              <label htmlFor="eng-name">Client / product name</label>
              <input
                id="eng-name"
                type="text"
                required
                placeholder="Acme Corp Portal"
                value={clientName}
                onInput={(e) => setClientName((e.target as HTMLInputElement).value)}
              />
            </div>
            <div class="field">
              <label htmlFor="eng-urls">URLs to scan</label>
              <textarea
                id="eng-urls"
                rows={3}
                placeholder="https://acme.com&#10;https://acme.com/checkout"
                value={urlsText}
                onInput={(e) => setUrlsText((e.target as HTMLTextAreaElement).value)}
              />
              <p class="muted small" style={{ marginTop: 4 }}>
                One per line (or comma-separated). The first is used as the homepage.
              </p>
            </div>
            <div class="field">
              <label htmlFor="eng-cadence">Recurring monitoring</label>
              <select
                id="eng-cadence"
                value={cadence}
                onChange={(e) => setCadence((e.target as HTMLSelectElement).value as Cadence)}
              >
                <option value="weekly">Weekly (Mondays 06:00 UTC)</option>
                <option value="daily">Daily (06:00 UTC)</option>
                <option value="monthly">Monthly (1st, 06:00 UTC)</option>
                <option value="none">One-time baseline only</option>
              </select>
            </div>
            {cadence !== 'none' && (
              <label class="reason" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={failOnRegression}
                  onChange={(e) => setFailOnRegression((e.target as HTMLInputElement).checked)}
                />
                <span>Alert (fail the run) when conformance regresses</span>
              </label>
            )}
            <div class="form-footer" style={{ marginTop: 12 }}>
              <button type="submit" disabled={busy}>
                {busy ? 'Onboarding…' : 'Create engagement'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
